import { useCallback, useEffect, useState } from 'react'

export interface UseFullscreenResult {
  fullscreen: boolean
  setFullscreen: (next: boolean) => void
  toggle: () => void
}

/**
 * Page-level fullscreen overlay state for table-heavy modules.
 *
 * Pair with `<FullscreenButton>` and apply the `fixed inset-0 z-50
 * flex flex-col bg-canvas` className to the page root when `fullscreen`
 * is true. Esc dismisses while fullscreen is active; the listener is
 * only attached during fullscreen so other overlays (drawers, modals)
 * keep their own Esc handlers.
 */
export function useFullscreen(initial = false): UseFullscreenResult {
  const [fullscreen, setFullscreen] = useState(initial)

  const toggle = useCallback(() => setFullscreen((v) => !v), [])

  useEffect(() => {
    if (!fullscreen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setFullscreen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [fullscreen])

  return { fullscreen, setFullscreen, toggle }
}
